/**
 * Generic retry utilities for handling eventually consistent APIs
 */

import type { AxiosError } from 'axios';
import { isTimingEnabled, logTiming } from './timingLogger';

export interface RetryConfig {
  maxRetries?: number;
  delays?: number[];
  onRetry?: (attempt: number, maxRetries: number) => void;
}

export const DEFAULT_RETRY_CONFIG: Required<RetryConfig> = {
  maxRetries: 6,
  delays: [500, 500, 750, 1000, 1500, 1500],
  onRetry: (attempt, maxRetries) => {
    console.log(`Retry attempt ${attempt}/${maxRetries}`);
  }
};

/**
 * Generic retry function that retries until a condition is met
 */
export async function retryUntilCondition<T>(
  fn: () => Promise<T>,
  condition: (result: T) => boolean,
  config?: RetryConfig
): Promise<T | null> {
  const { maxRetries, delays, onRetry } = { ...DEFAULT_RETRY_CONFIG, ...config };

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await fn();
      if (condition(result)) {
        return result;
      }

      // Condition not met, retry if we have attempts left
      if (attempt < maxRetries) {
        onRetry(attempt + 1, maxRetries);
        await new Promise(resolve => setTimeout(resolve, delays[attempt] || delays[delays.length - 1]));
      }
    } catch (error) {
      // For retryUntilCondition, we might want to continue on errors
      // depending on the use case
      if (attempt === maxRetries) {
        throw error;
      }
      onRetry(attempt + 1, maxRetries);
      await new Promise(resolve => setTimeout(resolve, delays[attempt] || delays[delays.length - 1]));
    }
  }

  return null;
}

/**
 * Wait for a resource to be created (404 -> success)
 * Retries until the resource exists (no longer returns 404)
 */
export async function waitForResourceCreation<T>(
  fetchFn: () => Promise<T>,
  config?: RetryConfig,
  validator?: (data: T) => boolean
): Promise<T | null> {
  const { maxRetries, delays, onRetry } = { ...DEFAULT_RETRY_CONFIG, ...config };

  const timingOn = isTimingEnabled();

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const t0 = timingOn ? performance.now() : 0;
    if (timingOn) logTiming(`START waitForCreation.attempt[${attempt}]`);

    try {
      const result = await fetchFn();

      // If we have a validator, use it to verify the data
      if (validator && !validator(result)) {
        console.error('Resource validation failed');
        if (timingOn) logTiming(`  END waitForCreation.attempt[${attempt}]`, `(${Math.round(performance.now() - t0)}ms) validation-failed`);
        return null;
      }

      // Resource exists and is valid
      if (timingOn) logTiming(`  END waitForCreation.attempt[${attempt}]`, `(${Math.round(performance.now() - t0)}ms) found`);
      return result;
    } catch (error) {
      const axiosError = error as AxiosError;

      // If we get a 404, the resource doesn't exist yet
      if (axiosError.response?.status === 404) {
        if (timingOn) logTiming(`  END waitForCreation.attempt[${attempt}]`, `(${Math.round(performance.now() - t0)}ms) 404`);
        if (attempt < maxRetries) {
          console.log(`Resource not found, retrying in ${delays[attempt]}ms (attempt ${attempt + 1}/${maxRetries})`);
          onRetry(attempt + 1, maxRetries);
          await new Promise(resolve => setTimeout(resolve, delays[attempt] || delays[delays.length - 1]));
        } else {
          console.log('Resource not found after all retries');
          return null;
        }
      } else {
        // For other errors, throw them
        if (timingOn) logTiming(`  END waitForCreation.attempt[${attempt}]`, `(${Math.round(performance.now() - t0)}ms) error`);
        throw error;
      }
    }
  }

  return null;
}

/**
 * Wait for a resource to be deleted (success -> 404)
 * Retries until the resource returns 404
 */
export async function waitForResourceDeletion(
  fetchFn: () => Promise<unknown>,
  config?: RetryConfig
): Promise<boolean> {
  const { maxRetries, delays, onRetry } = { ...DEFAULT_RETRY_CONFIG, ...config };

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      await fetchFn();
      // If we get here, the resource still exists
      if (attempt < maxRetries) {
        console.log(`Resource still exists, retrying in ${delays[attempt]}ms (attempt ${attempt + 1}/${maxRetries})`);
        onRetry(attempt + 1, maxRetries);
        await new Promise(resolve => setTimeout(resolve, delays[attempt] || delays[delays.length - 1]));
      }
    } catch (error) {
      const axiosError = error as AxiosError;

      // If we get a 404, the resource was successfully deleted
      if (axiosError.response?.status === 404) {
        return true;
      }

      // For other errors, throw them
      throw error;
    }
  }

  // Resource was not deleted after all retries
  return false;
}

/**
 * Wait for a resource to be updated
 * Can check either specific fields or lastUpdated timestamp
 */
export async function waitForResourceUpdate<T extends object>(
  fetchFn: () => Promise<T>,
  expectedValues?: Partial<T>,
  lastUpdatedField: string = 'lastUpdated',
  config?: RetryConfig
): Promise<T | null> {
  const { maxRetries, delays, onRetry } = { ...DEFAULT_RETRY_CONFIG, ...config };

  // Get initial state to compare lastUpdated if no expected values provided
  let initialLastUpdated: unknown;
  if (!expectedValues) {
    try {
      const initial = await fetchFn();
      initialLastUpdated = (initial as Record<string, unknown>)[lastUpdatedField];
    } catch {
      console.error('Failed to get initial state for update comparison');
      return null;
    }
  }

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await fetchFn();
      const resultRecord = result as Record<string, unknown>;

      if (expectedValues) {
        // Check if all expected fields match
        const allMatch = Object.entries(expectedValues).every(([key, expectedValue]) => {
          const actualValue = resultRecord[key];
          const matches = JSON.stringify(actualValue) === JSON.stringify(expectedValue);
          if (!matches && attempt === 0) {
            console.log(`Field ${key} mismatch: expected ${JSON.stringify(expectedValue)}, got ${JSON.stringify(actualValue)}`);
          }
          return matches;
        });

        if (allMatch) {
          return result;
        }
      } else if (initialLastUpdated && resultRecord[lastUpdatedField] !== initialLastUpdated) {
        // lastUpdated has changed, update is complete
        return result;
      }

      // Update not complete, retry if we have attempts left
      if (attempt < maxRetries) {
        console.log(`Update verification attempt ${attempt + 1}/${maxRetries} failed, retrying in ${delays[attempt]}ms`);
        onRetry(attempt + 1, maxRetries);
        await new Promise(resolve => setTimeout(resolve, delays[attempt] || delays[delays.length - 1]));
      }
    } catch {
      // For update verification, we typically want to continue retrying on errors
      if (attempt === maxRetries) {
        console.error('Update verification failed after all retries');
        return null;
      }
      onRetry(attempt + 1, maxRetries);
      await new Promise(resolve => setTimeout(resolve, delays[attempt] || delays[delays.length - 1]));
    }
  }

  console.warn('Update verification failed, returning last fetched result');
  return null;
}

/**
 * Wait for a specific field to have an expected value
 * Useful for status fields or state transitions
 */
export async function waitForFieldValue<T extends object>(
  fetchFn: () => Promise<T>,
  fieldPath: string,
  expectedValue: unknown,
  config?: RetryConfig
): Promise<T | null> {
  const { maxRetries, delays, onRetry } = { ...DEFAULT_RETRY_CONFIG, ...config };

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await fetchFn();

      // Handle nested field paths (e.g., "versions[0].status")
      const actualValue = getNestedValue(result, fieldPath);

      if (JSON.stringify(actualValue) === JSON.stringify(expectedValue)) {
        return result;
      }

      // Field doesn't match, retry if we have attempts left
      if (attempt < maxRetries) {
        console.log(`Field ${fieldPath} is ${JSON.stringify(actualValue)}, waiting for ${JSON.stringify(expectedValue)}. Retrying in ${delays[attempt]}ms (attempt ${attempt + 1}/${maxRetries})`);
        onRetry(attempt + 1, maxRetries);
        await new Promise(resolve => setTimeout(resolve, delays[attempt] || delays[delays.length - 1]));
      }
    } catch (error) {
      const axiosError = error as AxiosError;

      // If we get a 404, the resource doesn't exist
      if (axiosError.response?.status === 404) {
        console.error('Resource not found during field value check');
        return null;
      }

      // For other errors, we might want to retry
      if (attempt < maxRetries) {
        onRetry(attempt + 1, maxRetries);
        await new Promise(resolve => setTimeout(resolve, delays[attempt] || delays[delays.length - 1]));
      } else {
        throw error;
      }
    }
  }

  return null;
}

/**
 * Helper function to get nested object values using a path string
 * Supports array notation like "versions[0].status"
 */
function getNestedValue(obj: object, path: string): unknown {
  const keys = path.split(/\.|\[|\]/).filter(Boolean);
  return keys.reduce<unknown>((current, key) => {
    if (current === null || current === undefined) return undefined;
    // Handle array indices
    if (!isNaN(Number(key))) {
      return (current as Record<number, unknown>)[Number(key)];
    }
    return (current as Record<string, unknown>)[key];
  }, obj);
}